import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import CustomCursor from '@/components/CustomCursor';
import CodeRain from '@/components/CodeRain';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIUM_OPTIONS = [
  { value: 'digital_art', label: 'Digital Art' },
  { value: 'ai_art', label: 'AI Art' },
  { value: 'video', label: 'Video' },
  { value: 'photography', label: 'Photography' },
  { value: 'animation', label: 'Animation' },
  { value: 'immersive_media', label: 'Immersive Media' },
  { value: 'installation', label: 'Installation' },
  { value: 'new_media_art', label: 'New Media Art' },
  { value: 'other', label: 'Other' },
];

const ATTENDANCE_OPTIONS = [
  { value: 'yes', label: 'Yes, I can come to the venue' },
  { value: 'maybe', label: 'Maybe, unable to confirm at this time' },
  { value: 'remote', label: 'No, I can only participate remotely' },
];

const REQUIRED_FIELDS = [
  { key: 'artist_name', label: 'Please enter your artist name.' },
  { key: 'email', label: 'Please enter a valid email address.' },
  { key: 'main_medium_style', label: 'Please select your main medium.' },
  { key: 'location', label: 'Please enter your location.' },
  { key: 'artist_statement', label: 'Please write your artist statement.' },
  { key: 'artwork_link', label: 'Please share a link to the work you would like us to consider.' },
  { key: 'venue_attendance', label: 'Please select if you can attend the venue.' },
];

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function OpenCallPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const fieldRefs = useRef({});
  const [form, setForm] = useState({
    artist_name: '',
    email: '',
    main_medium_style: '',
    location: '',
    main_social_media: '',
    portfolio_link: '',
    artist_bio: '',
    artist_statement: '',
    artwork_link: '',
    venue_attendance: '',
    newsletter_opt_in: false,
  });

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    for (const { key, label } of REQUIRED_FIELDS) {
      if (!form[key] || !form[key].trim()) {
        newErrors[key] = label;
      }
    }
    if (form.email && !isValidEmail(form.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorKey = REQUIRED_FIELDS.find((f) => validationErrors[f.key])?.key;
      if (firstErrorKey && fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldRefs.current[firstErrorKey].focus?.();
      }
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/open-call-application`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen text-[#F5F5F5]">
      <CustomCursor />
      <CodeRain />

      <div className="relative z-10 px-6 sm:px-10 py-12 max-w-4xl mx-auto">
        {/* Back button */}
        <button
          data-testid="back-to-main"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase mb-12 transition-colors duration-300"
          style={{ cursor: 'pointer' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Main Site
        </button>

        {submitted ? (
          <SuccessMessage onBack={() => navigate('/')} />
        ) : (
          <>
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit">
                OPEN CALL
              </h1>
              <p className="text-base sm:text-lg font-outfit text-white mt-2 tracking-tight">
                This Is Humanity — Digital Art Exhibition and Event with Free Accommodation
              </p>
              <div className="mt-4 space-y-1 text-xs font-mono text-[#f6f6f6] tracking-[0.15em]">
                <p>THE RELATIVE GALLERY</p>
                <p>26 TO 28 JUNE 2026</p>
                <p>LENIWKA STARY MLYN, WESTERN POLAND</p>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-12 space-y-5 text-sm sm:text-base font-mono text-[#f6f6f6] leading-relaxed"
            >
              <p>
                The Relative Gallery invites artists working with digital art, AI art, video, animation, photography, immersive media, installation, and new media art to apply for the inaugural exhibition under the theme <strong className="text-white">This Is Humanity</strong>.
              </p>
              <p>
                The exhibition explores who we are as humans living through a new digital era. Creators, destroyers, dreamers, believers, bodies, systems, memories, and machines.
              </p>
              <p>
                The Relative Gallery is a new exhibition space and artist retreat located in the countryside of Western Poland, close to Swiebodzin and around 1.5 hours from Berlin. The gallery is housed inside a restored historical barn at Leniwka Stary Mlyn, combining a natural rural setting with a contemporary space for digital culture.
              </p>
              <p>
                Selected artists will be invited to present their work as part of a curated group exhibition using digital screens, projection, prints, installations, and immersive formats. The exhibition weekend will include an opening event, artist panels, chances to network with other artists and collectors, and a final closing evening event.
              </p>
              <p>
                For selected artists, The Relative Gallery will provide <strong className="text-white">free accommodation and food</strong> during the event. Artists will stay at Leniwka Stary Mlyn, a boutique countryside hotel surrounded by fields, water, and orchard landscape.
              </p>
              <p className="text-[#bbbbbb]">
                Transportation to and from the venue must be covered by the artists.
              </p>
              <p>
                The goal is to create more than an exhibition. It is a meeting point for artists, collectors, collaborators, and people interested in the future of art, technology, perception, and digital culture.
              </p>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16"
            >
              <div className="w-full h-px bg-[#222222] mb-12" />
              <h2 className="text-xl sm:text-2xl font-outfit font-semibold text-white tracking-tight mb-8">
                Open Call Application Form
              </h2>

              <form onSubmit={handleSubmit} data-testid="open-call-form" className="space-y-6" noValidate>
                <Field label="ARTIST NAME" error={errors.artist_name} required refCb={(el) => { fieldRefs.current.artist_name = el; }}>
                  <Input
                    data-testid="oc-artist-name"
                    type="text"
                    placeholder="Your artist name or public name"
                    value={form.artist_name}
                    onChange={(e) => updateField('artist_name', e.target.value)}
                    className={inputCls(errors.artist_name)}
                    ref={(el) => { fieldRefs.current.artist_name = el; }}
                  />
                </Field>

                <Field label="EMAIL ADDRESS" error={errors.email} required refCb={(el) => { fieldRefs.current.email = el; }}>
                  <Input
                    data-testid="oc-email"
                    type="email"
                    placeholder="Your email address so we can contact you"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className={inputCls(errors.email)}
                    ref={(el) => { fieldRefs.current.email = el; }}
                  />
                </Field>

                <Field label="MAIN MEDIUM / STYLE" error={errors.main_medium_style} required refCb={(el) => { fieldRefs.current.main_medium_style = el; }}>
                  <div ref={(el) => { fieldRefs.current.main_medium_style = el; }}>
                    <Select value={form.main_medium_style} onValueChange={(v) => updateField('main_medium_style', v)}>
                      <SelectTrigger data-testid="oc-medium" className={selectCls(errors.main_medium_style)}>
                        <SelectValue placeholder="Select your main medium" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#333333] rounded-none">
                        {MEDIUM_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="font-mono text-sm text-[#cccccc] focus:bg-[#1a1a1a] focus:text-white rounded-none">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Field>

                <Field label="LOCATION" error={errors.location} required refCb={(el) => { fieldRefs.current.location = el; }}>
                  <Input
                    data-testid="oc-location"
                    type="text"
                    placeholder="City and country"
                    value={form.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    className={inputCls(errors.location)}
                    ref={(el) => { fieldRefs.current.location = el; }}
                  />
                </Field>

                <Field label="MAIN SOCIAL MEDIA">
                  <Input
                    data-testid="oc-social"
                    type="text"
                    placeholder="Instagram, X, TikTok, website, or your main platform"
                    value={form.main_social_media}
                    onChange={(e) => updateField('main_social_media', e.target.value)}
                    className={inputCls()}
                  />
                </Field>

                <Field label="PORTFOLIO LINK">
                  <Input
                    data-testid="oc-portfolio"
                    type="url"
                    placeholder="Website, Behance, Vimeo, ArtStation, or portfolio link"
                    value={form.portfolio_link}
                    onChange={(e) => updateField('portfolio_link', e.target.value)}
                    className={inputCls()}
                  />
                </Field>

                <Field label="ARTIST BIO">
                  <textarea
                    data-testid="oc-bio"
                    placeholder="Tell us about your background, past exhibitions, professional experience"
                    value={form.artist_bio}
                    onChange={(e) => updateField('artist_bio', e.target.value)}
                    rows={4}
                    className={textareaCls()}
                  />
                </Field>

                <Field label="ARTIST STATEMENT" error={errors.artist_statement} required refCb={(el) => { fieldRefs.current.artist_statement = el; }}>
                  <textarea
                    data-testid="oc-statement"
                    placeholder="Tell us about your artistic practice, what themes you explore, why you do what you do, and why your work connects with the theme: This Is Humanity"
                    value={form.artist_statement}
                    onChange={(e) => updateField('artist_statement', e.target.value)}
                    rows={5}
                    className={textareaCls(errors.artist_statement)}
                    ref={(el) => { fieldRefs.current.artist_statement = el; }}
                  />
                </Field>

                <Field label="WORK / ARTWORK LINK" error={errors.artwork_link} required refCb={(el) => { fieldRefs.current.artwork_link = el; }}>
                  <Input
                    data-testid="oc-artwork"
                    type="url"
                    placeholder="Google Drive, Dropbox, Vimeo, website, or portfolio link"
                    value={form.artwork_link}
                    onChange={(e) => updateField('artwork_link', e.target.value)}
                    className={inputCls(errors.artwork_link)}
                    ref={(el) => { fieldRefs.current.artwork_link = el; }}
                  />
                </Field>

                <Field label="ARE YOU ABLE TO COME TO THE VENUE?" error={errors.venue_attendance} required refCb={(el) => { fieldRefs.current.venue_attendance = el; }}>
                  <div ref={(el) => { fieldRefs.current.venue_attendance = el; }}>
                    <Select value={form.venue_attendance} onValueChange={(v) => updateField('venue_attendance', v)}>
                      <SelectTrigger data-testid="oc-attendance" className={selectCls(errors.venue_attendance)}>
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#333333] rounded-none">
                        {ATTENDANCE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="font-mono text-sm text-[#cccccc] focus:bg-[#1a1a1a] focus:text-white rounded-none">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Field>

                <div className="pt-6">
                  {/* GDPR-compliant newsletter opt-in — unchecked by default. */}
                  <label
                    className="flex items-start gap-3 mb-5 cursor-pointer group"
                    data-testid="oc-newsletter-optin-label"
                  >
                    <input
                      type="checkbox"
                      checked={form.newsletter_opt_in}
                      onChange={(e) => updateField('newsletter_opt_in', e.target.checked)}
                      data-testid="oc-newsletter-optin"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#90E0EF] cursor-pointer"
                    />
                    <span className="text-[12px] font-mono text-[#f0f0f0] leading-[1.7] group-hover:text-white transition-colors">
                      Yes, I&apos;d like to receive occasional emails from The Relative Gallery about future open calls, exhibitions and artist opportunities.
                      <span className="block mt-1 text-[10px] text-[#888]">
                        Optional. You can unsubscribe at any time via the link in every email.
                      </span>
                    </span>
                  </label>

                  <button
                    data-testid="oc-submit-button"
                    type="submit"
                    disabled={loading}
                    className="open-call-btn w-full sm:w-auto rounded-none uppercase tracking-[0.15em] text-[11px] font-mono font-medium h-12 px-10 bg-[#90E0EF] text-black hover:bg-[#a8e8f4] transition-all duration-300 hover:shadow-[0_0_24px_rgba(144,224,239,0.35)] hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-none"
                    style={{ cursor: loading ? 'wait' : 'pointer' }}
                  >
                    {loading ? 'SUBMITTING...' : 'Apply to The Relative Gallery'}
                  </button>
                </div>
              </form>
            </motion.div>

            {/* Bottom back button */}
            <div className="mt-16 pt-8 border-t border-[#1a1a1a]">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300"
                style={{ cursor: 'pointer' }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Main Site
              </button>
            </div>
          </>
        )}
      </div>

      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: '#111111',
            border: '1px solid #333333',
            color: '#F5F5F5',
            borderRadius: 0,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '12px',
          },
        }}
      />
    </div>
  );
}

function SuccessMessage({ onBack }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="open-call-success"
      className="py-12 space-y-5 text-sm sm:text-base font-mono text-[#f6f6f6] leading-relaxed"
    >
      <h1 className="text-white font-outfit text-2xl sm:text-3xl font-semibold tracking-tight">
        We have received your submission.
      </h1>
      <p>
        Thank you for applying to This Is Humanity, the inaugural exhibition of The Relative Gallery. We really appreciate your interest in being part of what we are creating.
      </p>
      <p>
        Artists will be selected based on their compatibility with the exhibition theme.
      </p>
      <p>
        The curation process will finish around the beginning of June. We will be in touch.
      </p>
      <p className="mt-8">
        Best,<br />
        <strong className="text-white">The Relative Gallery Team</strong>
      </p>
      <div className="pt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#f6f6f6] hover:text-white font-mono text-xs tracking-[0.15em] uppercase transition-colors duration-300"
          style={{ cursor: 'pointer' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Main Site
        </button>
      </div>
    </motion.div>
  );
}

function Field({ label, error, required, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono text-[#f6f6f6] tracking-[0.2em] uppercase block mb-2">
        {label}{required && <span className="text-[#90E0EF] ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-[11px] font-mono text-red-400 mt-1.5">{error}</p>
      )}
    </div>
  );
}

function inputCls(error) {
  return `bg-transparent border-${error ? 'red-400' : '[#333333]'} text-white rounded-none font-mono text-sm placeholder:text-[#555555] focus-visible:ring-white h-11 ${error ? 'border-red-400' : ''}`;
}

function selectCls(error) {
  return `bg-transparent border-${error ? 'red-400' : '[#333333]'} text-white rounded-none font-mono text-sm h-11 focus:ring-white ${error ? 'border-red-400' : ''}`;
}

function textareaCls(error) {
  return `w-full bg-transparent border ${error ? 'border-red-400' : 'border-[#333333]'} text-white rounded-none font-mono text-sm placeholder:text-[#555555] focus-visible:ring-white focus-visible:ring-1 focus-visible:outline-none p-3 resize-none`;
}
